/**
 * Month view — calendar grid with event chips + side panel.
 *
 * Preserves the current CalendarPage 2-column UX: month grid on the left,
 * selected day/range meeting list on the right.
 *
 * @module components/calendar/MonthView
 */

import { useMemo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  startOfDay,
  endOfDay,
  eachDayOfInterval,
  isSameMonth,
  addDays,
  subDays,
  getDay,
  setDate,
  isWithinInterval,
  parseISO,
} from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import {
  Calendar as CalendarIcon,
  Clock,
  Users,
  ChevronDown,
  Plus,
} from 'lucide-react'
import {
  groupEventsByDay,
  getStatusColor,
  getMeetingTitle,
  isSameDayTz,
} from '../../utils/calendarLayout'
import { expandRecurringMeetings } from '../../utils/expandRecurringMeetings'

const INITIAL_MEETINGS_SHOWN = 15

const QUICK_SELECT_OPTIONS = [
  { id: 'business-week', label: 'Business week' },
  { id: 'full-week', label: 'Full week' },
  { id: 'business-month', label: 'Business month' },
  { id: 'full-month', label: 'Full month' },
]

function EventChip({ meeting, userTz }) {
  const colors = getStatusColor(meeting.status, meeting.isDraft)
  const title = getMeetingTitle(meeting)
  const startStr = meeting.startTime || meeting.scheduledStartTime
  const timeLabel = startStr
    ? format(utcToZonedTime(parseISO(startStr), userTz), 'h:mma').toLowerCase()
    : ''

  return (
    <div
      className={`text-[11px] leading-tight truncate px-1 py-px rounded border-l-2 ${colors.bg} ${colors.border} ${colors.text} ${colors.draftClass}`}
    >
      {timeLabel && <span className="opacity-70 mr-0.5">{timeLabel}</span>}
      <span className="font-medium">{title}</span>
    </div>
  )
}

export default function MonthView({
  meetings,
  currentDate,
  userTz,
  onDayClick,
  selectedDate,
  selectedRange,
  onDateSelect,
  onRangeSelect,
  meetingsListExpanded,
  onToggleExpanded,
  calendarMeetings,
  fetchError,
  onRetry,
}) {
  const dateInTz = utcToZonedTime(currentDate, userTz)

  // Calendar grid days
  const days = useMemo(() => {
    const ms = startOfMonth(dateInTz)
    const me = endOfMonth(dateInTz)
    return eachDayOfInterval({ start: startOfWeek(ms), end: endOfWeek(me) })
  }, [dateInTz])

  // Memoized day → meetings map (fixes 42x filter perf bug + sort bug)
  const dayMap = useMemo(() => groupEventsByDay(meetings, userTz), [meetings, userTz])

  // Selected day meetings
  const selectedDayMeetings = useMemo(() => {
    const key = format(utcToZonedTime(selectedDate, userTz), 'yyyy-MM-dd')
    return dayMap.get(key) || []
  }, [dayMap, selectedDate, userTz])

  // Range meetings (for quick-select)
  const meetingsInRange = useMemo(() => {
    if (!selectedRange) return selectedDayMeetings
    const { start, end, weekdaysOnly } = selectedRange
    const rangeStart = startOfDay(start)
    const rangeEnd = endOfDay(end)
    const rangeExpanded = calendarMeetings
      ? expandRecurringMeetings(calendarMeetings, rangeStart, rangeEnd)
      : meetings
    return rangeExpanded
      .filter((m) => {
        const startStr = m.startTime ?? m.scheduledStartTime
        if (!startStr) return false
        const d = utcToZonedTime(parseISO(startStr), userTz)
        const dStart = startOfDay(d)
        if (!isWithinInterval(dStart, { start: rangeStart, end: rangeEnd })) return false
        if (weekdaysOnly) {
          const dayOfWeek = getDay(d)
          if (dayOfWeek === 0 || dayOfWeek === 6) return false
        }
        return true
      })
      .sort((a, b) => {
        const sa = a.startTime ?? a.scheduledStartTime
        const sb = b.startTime ?? b.scheduledStartTime
        return new Date(sa).getTime() - new Date(sb).getTime()
      })
  }, [calendarMeetings, meetings, selectedDayMeetings, selectedRange, userTz])

  const displayedMeetings = meetingsListExpanded
    ? meetingsInRange
    : meetingsInRange.slice(0, INITIAL_MEETINGS_SHOWN)
  const hasMoreMeetings = meetingsInRange.length > INITIAL_MEETINGS_SHOWN
  const remainingCount = meetingsInRange.length - INITIAL_MEETINGS_SHOWN

  // Quick-select handler
  const handleQuickSelect = useCallback(
    (id) => {
      const now = utcToZonedTime(new Date(), userTz)
      const sun = startOfWeek(now, { weekStartsOn: 0 })
      const mon = startOfWeek(now, { weekStartsOn: 1 })
      const ms = startOfMonth(now)
      const me = endOfMonth(now)
      const firstBizDay =
        getDay(ms) === 0 ? setDate(ms, 2) : getDay(ms) === 6 ? setDate(ms, 3) : ms
      const lastBizDay =
        getDay(me) === 0 ? subDays(me, 2) : getDay(me) === 6 ? subDays(me, 1) : me

      let date, rangeStart, rangeEnd, weekdaysOnly = false
      switch (id) {
        case 'business-week':
          date = mon; rangeStart = startOfDay(mon); rangeEnd = endOfDay(addDays(mon, 4)); break
        case 'full-week':
          date = sun; rangeStart = startOfDay(sun); rangeEnd = endOfDay(addDays(sun, 6)); break
        case 'business-month':
          date = firstBizDay; rangeStart = startOfDay(firstBizDay); rangeEnd = endOfDay(lastBizDay); weekdaysOnly = true; break
        case 'full-month':
          date = ms; rangeStart = startOfDay(ms); rangeEnd = endOfDay(me); break
        default:
          date = now; rangeStart = startOfDay(now); rangeEnd = endOfDay(now)
      }
      onDateSelect(date)
      onRangeSelect({ start: rangeStart, end: rangeEnd, weekdaysOnly })
    },
    [onDateSelect, onRangeSelect, userTz]
  )

  const today = utcToZonedTime(new Date(), userTz)

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Left: Month grid */}
      <div className="lg:col-span-2 glass rounded-2xl p-4 sm:p-6">
        {/* Quick select */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {QUICK_SELECT_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => handleQuickSelect(opt.id)}
              className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Weekday headers */}
        <div className="grid grid-cols-7 gap-0.5 md:gap-1 mb-1">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
            <div key={d} className="text-center text-xs text-gray-400 py-1.5">
              {d}
            </div>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-7 gap-0.5 md:gap-1">
          {days.map((day) => {
            const key = format(day, 'yyyy-MM-dd')
            const dayMeetings = dayMap.get(key) || []
            const isCurrentMonth = isSameMonth(day, dateInTz)
            const isSingleDaySelected = !selectedRange && isSameDayTz(day, selectedDate, userTz)
            const isToday = isSameDayTz(day, today, userTz)
            const dayOfWeek = getDay(day)
            const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5
            const isInRange =
              selectedRange &&
              isWithinInterval(startOfDay(day), {
                start: selectedRange.start,
                end: selectedRange.end,
              }) &&
              (!selectedRange.weekdaysOnly || isWeekday)

            return (
              <button
                key={key}
                type="button"
                onClick={() => onDateSelect(day)}
                className={`
                  relative min-h-[80px] md:min-h-[100px] p-1.5 rounded-lg text-left transition-all overflow-hidden
                  ${isCurrentMonth ? 'text-gray-900' : 'text-gray-300'}
                  ${isSingleDaySelected ? 'bg-gradient-primary text-white' : ''}
                  ${isInRange && !isSingleDaySelected ? 'bg-primary-50' : ''}
                  ${!isSingleDaySelected && !isInRange ? 'hover:bg-gray-50' : ''}
                  ${isToday ? 'ring-2 ring-primary-500 ring-inset' : ''}
                `}
              >
                {/* Date number */}
                <div className="flex items-center justify-between mb-0.5">
                  <span
                    className={`text-sm inline-flex items-center justify-center ${
                      isToday && !isSingleDaySelected
                        ? 'bg-primary-500 text-white rounded-full w-6 h-6'
                        : ''
                    } ${isSingleDaySelected ? 'font-semibold' : ''}`}
                  >
                    {format(day, 'd')}
                  </span>
                </div>

                {/* Event chips (up to 3) */}
                {isCurrentMonth && dayMeetings.length > 0 && (
                  <div className="space-y-px">
                    {dayMeetings.slice(0, 3).map((m, i) => (
                      <EventChip
                        key={m.id ?? m.meetingId ?? i}
                        meeting={m}
                        userTz={userTz}
                      />
                    ))}
                    {dayMeetings.length > 3 && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          onDayClick(day)
                        }}
                        className={`text-[10px] font-medium px-1 ${
                          isSingleDaySelected ? 'text-white/80' : 'text-primary-600'
                        }`}
                      >
                        +{dayMeetings.length - 3} more
                      </button>
                    )}
                  </div>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* Right: Side panel */}
      <div className="space-y-6">
        <div className="glass rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {selectedRange
              ? `${format(selectedRange.start, 'MMM d')} – ${format(selectedRange.end, 'MMM d, yyyy')}`
              : format(utcToZonedTime(selectedDate, userTz), 'EEEE, MMMM d')}
          </h3>

          {/* Error state */}
          {fetchError && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm mb-4 flex items-center justify-between">
              <span>Failed to load</span>
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="px-2 py-1 rounded text-xs font-medium bg-red-100 hover:bg-red-200"
                >
                  Retry
                </button>
              )}
            </div>
          )}

          {displayedMeetings.length > 0 ? (
            <div className="space-y-3">
              {displayedMeetings.map((meeting) => {
                const start = meeting.startTime ?? meeting.scheduledStartTime
                return (
                  <Link
                    key={meeting.id ?? meeting.meetingId}
                    to={`/meeting/${meeting.id ?? meeting.meetingId}`}
                    className="block p-4 rounded-xl bg-gray-50 border border-gray-100 hover:border-gray-200 transition-all"
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`w-1 h-12 rounded-full ${
                          getStatusColor(meeting.status, meeting.isDraft).dot
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 mb-1 truncate">
                          {getMeetingTitle(meeting)}
                          {meeting.isDraft && (
                            <span className="ml-2 text-xs font-normal text-gray-400 border border-dashed border-gray-300 rounded px-1">
                              Draft
                            </span>
                          )}
                        </h4>
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                          {start && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Intl.DateTimeFormat('en-US', {
                                timeZone: userTz,
                                month: 'short',
                                day: 'numeric',
                              }).format(parseISO(start))}
                              ,{' '}
                              {new Intl.DateTimeFormat('en-US', {
                                timeZone: userTz,
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true,
                                timeZoneName: 'shortGeneric',
                              }).format(parseISO(start))}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {meeting.participantCount ??
                              meeting.participants?.length ??
                              0}{' '}
                            participants
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                )
              })}
              {hasMoreMeetings && !meetingsListExpanded && (
                <button
                  type="button"
                  onClick={onToggleExpanded}
                  className="w-full py-2.5 rounded-lg text-sm font-medium text-primary-600 hover:bg-primary-50 transition-colors flex items-center justify-center gap-1"
                >
                  <ChevronDown className="w-4 h-4" />
                  Show more ({remainingCount} more)
                </button>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <CalendarIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500 mb-4">
                {selectedRange
                  ? 'No meetings in this period'
                  : 'No meetings scheduled'}
              </p>
              <Link
                to="/agenda"
                className="btn-secondary text-sm inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create Meeting
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
